import { isTemplate } from '@flow/libs/agreement';
import { T } from '@app/components/translation';
import toast from '@app/components/toasts';
import { UNASSIGNED_SIGNATURE_FIELDS_TOAST_ID } from '@app/components/contract-boxes/pdf-box/pdf-file/pdf-viewer/pdf-page/overlay-field/overlay-signature-field/unassigned-fields-toast';
import { EMPTY_EDITABLE_REQUIRED_FIELDS_TOAST_ID } from '@app/components/document-call-to-actions/actions/buttons-call-to-action/buttons/sign/helpers';

export const showTemplateSaveSuccessToast = () =>
  toast.info({
    id: 'saved-template',
    title: (
      <T
        _str="Saved"
        _comment="Title for the info message when the user has saved the template."
      />
    ),
    description: (
      <T
        _str="Template was successfully saved"
        _comment="Description for the info message when the user has saved."
      />
    ),
    duration: 5000,
  });

export const showDocumentSaveSuccessToast = () =>
  toast.info({
    id: 'saved-document',
    title: (
      <T
        _str="Saved"
        _comment="Title for the info message when the user has saved the document."
      />
    ),
    description: (
      <T
        _str="Document was successfully saved"
        _comment="Description for the info message when the user has saved."
      />
    ),
    duration: 5000,
  });

export const showSaveSuccessToast = (agreement: Oneflow.Agreement) => {
  const isInTemplate = isTemplate(agreement);
  return isInTemplate ? showTemplateSaveSuccessToast() : showDocumentSaveSuccessToast();
};

export const showSignaturesResetToast = () =>
  toast.warning({
    id: 'signatures-reset',
    title: (
      <T
        _str="Signatures reset"
        _comment="Title for the info message when the signatures have been reset."
      />
    ),
    description: (
      <T
        _str="The document needs to be signed again."
        _comment="Description text for the info message when the signatures have been reset."
      />
    ),
    duration: 5000,
  });

export const showApprovalsResetToast = () =>
  toast.warning({
    id: 'approvals-reset',
    title: (
      <T
        _str="Approvals reset"
        _comment="Title for the info message when the approvals have been reset."
      />
    ),
    description: (
      <T
        _str="The document needs to be approved again."
        _comment="Description text for the info message when the approvals have been reset."
      />
    ),
    duration: 5000,
  });

export const showApprovalsSignaturesResetToast = () =>
  toast.warning({
    id: 'approvals-signatures-reset',
    title: (
      <T
        _str="Approvals and signatures reset"
        _comment="Title for the info message when the approvals and signatures have been reset."
      />
    ),
    description: (
      <T
        _str="The document needs to be approved and signed again."
        _comment="Description text for the info message when the approvals and signatures have been reset."
      />
    ),
    duration: 5000,
  });

export const showEmptyRequiredFieldsToast = (editableEmptyRequiredElementsCount: number) =>
  toast.update({
    id: EMPTY_EDITABLE_REQUIRED_FIELDS_TOAST_ID,
    description: (
      <T
        _str="Fill in {count} required fields to proceed"
        count={editableEmptyRequiredElementsCount}
        _comment="Notification message for missing required fields"
      />
    ),
  });

export const showUnassignedSignatureFieldsToast = (unassignedFields: any) =>
  toast.update({
    id: UNASSIGNED_SIGNATURE_FIELDS_TOAST_ID,
    description: (
      <T
        _str="You have {count} unassigned Signature fields."
        count={unassignedFields.length}
        _comment="Info text explaining that if the user makes changes to the document or to the participants, the signatures might be reset."
      />
    ),
  });

export const showPreviewModeSaveToast = () =>
  toast.warning({
    id: 'preview-mode-save',
    title: (
      <T
        _str="Your changes won't be saved in preview"
        _comment="Title for the info message when the user clicks save in preview mode."
      />
    ),
    duration: 5000,
  });

export const showSavedAndNotifiedToast = () =>
  toast.info({
    id: 'saved-and-notified',
    title: (
      <T
        _str="Saved and notified"
        _comment="Title for the info message when the user has saved the document."
      />
    ),
    description: (
      <T
        _str="Document successfully saved and participants have been notified"
        _comment="Description text for the info message when the user has saved."
      />
    ),
    duration: 5000,
  });
